import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Search, MoreHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';

const VISIBLE_LIMIT = 10;

const FeatureAccessOverview = ({ payload, search, onSearchChange, onWorkspaceClick, onGrantClick }) => {
  const [expanded, setExpanded] = useState({});
  const [showAll, setShowAll] = useState({});

  const toggle = (planKey) => setExpanded((e) => ({ ...e, [planKey]: !e[planKey] }));

  const matchesSearch = (workspace) => {
    if (!search) return true;
    const q = search.toLowerCase();
    if (workspace.name.toLowerCase().includes(q)) return true;
    return (workspace.overrides || []).some((o) =>
      o.sub_feature_label.toLowerCase().includes(q) || o.sub_feature_code.toLowerCase().includes(q)
    );
  };

  const renderPlanGroup = (plan, planKey) => {
    const open = expanded[planKey];
    const visibleAll = showAll[planKey];
    const ws = (plan.workspaces || []).filter(matchesSearch);
    const visible = visibleAll ? ws : ws.slice(0, VISIBLE_LIMIT);
    const overflow = ws.length - visible.length;
    const includedSummary = (plan.included_sub_feature_codes || []).slice(0, 6).join(' · ');

    return (
      <div key={planKey} className="border border-border rounded-lg mb-4 overflow-hidden">
        <div
          className="px-4 py-3 bg-muted/50 cursor-pointer flex items-center justify-between"
          onClick={() => toggle(planKey)}
        >
          <div className="flex items-center gap-2">
            {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            <span className="text-sm font-semibold text-foreground uppercase">
              {plan.name || 'Workspaces without a plan'}
            </span>
            <span className="text-xs text-muted-foreground">— {ws.length} workspace{ws.length === 1 ? '' : 's'}</span>
          </div>
          {plan.id && (
            <Link
              to={`/admin/plans/${plan.id}/feature-assignments`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-foreground hover:underline"
            >
              Edit plan features →
            </Link>
          )}
        </div>

        {open && (
          <div>
            {includedSummary && (
              <div className="px-4 py-2 text-xs text-muted-foreground border-b border-border">
                Includes: {includedSummary}
                {plan.included_sub_feature_codes?.length > 6 ? `, +${plan.included_sub_feature_codes.length - 6} more` : ''}
              </div>
            )}
            <ul className="divide-y divide-gray-100">
              {visible.map((w) => (
                <li
                  key={w.id}
                  className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                  onClick={() => onWorkspaceClick(w)}
                >
                  <span className="text-sm text-foreground">{w.name}</span>
                  <div className="flex items-center gap-3">
                    {w.override_count === 0 ? (
                      <span className="text-xs text-muted-foreground">no overrides</span>
                    ) : (
                      <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                        {w.override_count} override{w.override_count === 1 ? '' : 's'}
                        {w.overrides?.[0] && ` · ${w.overrides[0].sub_feature_label}${w.overrides[0].state === 'enabled' ? ' ⊕' : ' ⊖'}`}
                      </span>
                    )}
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                  </div>
                </li>
              ))}
              {overflow > 0 && (
                <li className="px-4 py-2 text-xs text-muted-foreground text-center cursor-pointer hover:bg-muted/50"
                    onClick={() => setShowAll((s) => ({ ...s, [planKey]: true }))}>
                  Show all {ws.length}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search workspaces or features"
            className="pl-9 pr-3 py-2 w-full text-sm border border-input rounded-lg focus:ring-black focus:border-black"
          />
        </div>
        <button
          onClick={onGrantClick}
          className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800"
        >
          + Grant override
        </button>
      </div>

      {(payload.plans || []).map((plan) => renderPlanGroup(plan, `plan-${plan.id}`))}

      {(payload.unplanned_workspaces || []).length > 0 &&
        renderPlanGroup({
          id: null,
          name: 'Workspaces without a plan',
          included_sub_feature_codes: [],
          workspaces: payload.unplanned_workspaces || [],
        }, 'unplanned')}
    </div>
  );
};

export default FeatureAccessOverview;
